import React from 'react';

import type { DrawReactComponentProps } from '@aline/common';
import { getCheckboxOptionFieldId } from '@aline/common';
import { equals } from 'ramda';
import { useGetFieldDisabledReason } from 'src/editor/popovers';
import { suspendSelectionScroll } from 'src/editor/transforms/scroll-transforms';
import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import {
  useAuthorizedTransforms,
  useFieldFromCache,
  useGetCurrentDoc,
  useGetEditorUserState,
} from 'src/hooks';
import { useEditor, useSlateSelector } from 'src/layout-engine/hooks';
import { useEditingMode } from 'src/store/document/selectors';

const invisibleCheckboxStyle: React.CSSProperties = {
  inset: 0,
  margin: 0,
  opacity: 0,
  position: 'absolute',
};

export function CheckboxButton({
  height,
  isChecked,
  isDisabled,
  optionId,
  width,
}: DrawReactComponentProps['CheckboxButton']) {
  const editor = useEditor();
  const currentUserId = useCurrentUserId();
  const { editingMode } = useEditingMode();
  const { isReadOnly } = useGetEditorUserState();
  const { document } = useGetCurrentDoc();
  const { AuthorizedTransforms } = useAuthorizedTransforms();

  const fieldId = useSlateSelector(e => getCheckboxOptionFieldId(e, optionId), equals);
  const { field, loading } = useFieldFromCache(fieldId);
  const { disabledReason } = useGetFieldDisabledReason(field);

  const isContract = document?.variant === Apollo.DocumentVariant.Document;
  const cannotToggle =
    isDisabled || isReadOnly || (Boolean(fieldId) && (loading || Boolean(disabledReason)));

  if (!currentUserId || editingMode === null) return null;

  return (
    <label
      style={{
        cursor: cannotToggle ? 'default' : 'pointer',
        display: 'block',
        height,
        position: 'relative',
        width,
      }}
      onMouseDown={stopPropagation}
      onMouseUp={stopPropagation}
    >
      <input
        checked={isChecked}
        disabled={cannotToggle}
        style={{
          ...invisibleCheckboxStyle,
          cursor: cannotToggle ? 'default' : 'pointer',
        }}
        type="checkbox"
        onChange={() => {
          if (cannotToggle) return;
          suspendSelectionScroll();
          AuthorizedTransforms.toggleCheckbox(editor, {
            assigneeId: isContract ? (field?.action?.assignee?.id ?? null) : null,
            authorId: currentUserId,
            editingMode,
            optionId,
          });
        }}
        onClick={stopPropagation}
      />
    </label>
  );
}

function stopPropagation(event: React.SyntheticEvent): void {
  event.stopPropagation();
}
